import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { createInventoryItem, INVENTORY_ITEM_STATUS } from '../services/inventoryItemService';

/**
 * AddInventoryItemDialog
 * Dialog for creating a new inventory item (IMEI/Serial Number)
 * 
 * Features:
 * - Single item creation
 * - IMEI and Serial Number fields
 * - Status selection
 * - Purchase cost and date
 * - Notes
 * - Validation
 */
const AddInventoryItemDialog = ({
  open,
  onClose,
  variantId,
  variantName = 'Variant',
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Form state
  const [formData, setFormData] = useState({
    imei: '',
    serialNumber: '',
    status: 1, // Available by default
    purchaseCost: '',
    purchaseDate: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.imei && !formData.serialNumber) {
      newErrors.general = 'At least one of IMEI or Serial Number is required';
    }

    if (formData.imei && formData.imei.length < 15) {
      newErrors.imei = 'IMEI must be at least 15 characters';
    }

    if (formData.purchaseCost && isNaN(parseFloat(formData.purchaseCost))) {
      newErrors.purchaseCost = 'Purchase cost must be a valid number';
    }

    if (formData.purchaseCost && parseFloat(formData.purchaseCost) < 0) {
      newErrors.purchaseCost = 'Purchase cost cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        productVariantId: variantId,
        imei: formData.imei || null,
        serialNumber: formData.serialNumber || null,
        status: formData.status,
        purchaseCost: formData.purchaseCost ? parseFloat(formData.purchaseCost) : null,
        purchaseDate: formData.purchaseDate || null,
        notes: formData.notes || null,
      };

      console.log('Creating inventory item:', payload);
      await createInventoryItem(payload);

      toast.success('Inventory item created successfully');
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating inventory item:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        'Failed to create inventory item';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!loading) {
      setFormData({
        imei: '',
        serialNumber: '',
        status: 1,
        purchaseCost: '',
        purchaseDate: '',
        notes: '',
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: (e) => {
          e.preventDefault();
          handleSubmit();
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Add Inventory Item
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {variantName}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Stack spacing={2}>
          {/* General Error */}
          {errors.general && (
            <Alert severity="error" onClose={() => setErrors({})}>
              {errors.general}
            </Alert>
          )}

          {/* IMEI */}
          <TextField
            label="IMEI"
            placeholder="Enter 15-digit IMEI number"
            value={formData.imei}
            onChange={(e) => handleChange('imei', e.target.value)}
            error={Boolean(errors.imei)}
            helperText={errors.imei || 'Optional if Serial Number is provided'}
            fullWidth
            inputProps={{ maxLength: 20 }}
          />

          {/* Serial Number */}
          <TextField
            label="Serial Number"
            placeholder="Enter serial number"
            value={formData.serialNumber}
            onChange={(e) => handleChange('serialNumber', e.target.value)}
            error={Boolean(errors.serialNumber)}
            helperText={errors.serialNumber || 'Optional if IMEI is provided'}
            fullWidth
            inputProps={{ maxLength: 50 }}
          />

          {/* Status */}
          <TextField
            select
            label="Status"
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            fullWidth
          >
            {Object.entries(INVENTORY_ITEM_STATUS).map(([key, status]) => (
              <MenuItem key={key} value={parseInt(key)}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={2}>
            {/* Purchase Cost */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Purchase Cost"
                type="number"
                placeholder="0.00"
                value={formData.purchaseCost}
                onChange={(e) => handleChange('purchaseCost', e.target.value)}
                error={Boolean(errors.purchaseCost)}
                helperText={errors.purchaseCost}
                fullWidth
                InputProps={{
                  startAdornment: <span style={{ marginRight: 4 }}>€</span>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Purchase Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Purchase Date"
                type="date"
                value={formData.purchaseDate}
                onChange={(e) => handleChange('purchaseDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Notes */}
          <TextField
            label="Notes"
            placeholder="Additional notes..."
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            helperText={`${formData.notes.length}/500 characters`}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInventoryItemDialog;
